import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, XCircle, Activity, Database,
  Cpu, Smartphone, Globe, Loader2, ArrowLeft, RefreshCw, Clock, Bot, FlaskConical,
} from 'lucide-react';
import PageMeta from '../components/PageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  operational: { color: '#10B981', icon: CheckCircle2, label: 'Operational' },
  degraded:    { color: '#F59E0B', icon: AlertTriangle, label: 'Degraded' },
  outage:      { color: '#EF4444', icon: XCircle,       label: 'Outage' },
};

const COMPONENT_ICON = {
  'Web app':              Globe,
  'API':                  Activity,
  'Database':             Database,
  'AI Practice Partners': Cpu,
  'Mobile (Android)':     Smartphone,
};

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  } catch (_) { return ''; }
}

const StatusBadge = ({ status, size = 'md' }) => {
  const meta = STATUS_META[status] || STATUS_META.operational;
  const Icon = meta.icon;
  const dim = size === 'lg' ? 24 : 18;
  return (
    <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: meta.color }}>
      <Icon size={dim} />
      <span>{meta.label}</span>
    </span>
  );
};

const StatusPage = () => {
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [sumRes, incRes] = await Promise.all([
        fetch(`${API_URL}/api/status/summary`),
        fetch(`${API_URL}/api/status/incidents?limit=10`),
      ]);
      if (!sumRes.ok) throw new Error(`Summary endpoint returned ${sumRes.status}`);
      const sumData = await sumRes.json();
      setData(sumData);
      if (incRes.ok) {
        const incData = await incRes.json();
        setIncidents(incData.incidents || []);
      }
      setErr(null);
    } catch (e) {
      setErr(e.message || 'Failed to load status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    // Auto-refresh every 30s — matches industry-standard status pages
    const t = setInterval(fetchAll, 30_000);
    return () => clearInterval(t);
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white" data-testid="status-page-loader">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const overallMeta = STATUS_META[data?.overall] || STATUS_META.operational;

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white" data-testid="status-page">
      <PageMeta
        slug="status"
        path="/status"
        title="HiBFF Status"
        description="Real-time uptime, components, last 90 days of incidents. Auto-refreshes every 30 seconds."
      />
      <header className="px-6 sm:px-12 py-6 max-w-5xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-gray-900" data-testid="status-back-home">
          <ArrowLeft size={18} /> <span className="font-medium">HiBFF home</span>
        </Link>
        <button
          onClick={fetchAll}
          disabled={refreshing}
          data-testid="status-refresh-btn"
          aria-label="Refresh status"
          className="p-2 rounded-full hover:bg-white text-gray-600 transition disabled:opacity-50"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </header>

      <main className="px-6 sm:px-12 max-w-5xl mx-auto pb-20">
        {/* Hero status card */}
        <div
          className="rounded-2xl p-8 sm:p-12 mb-8 shadow-sm border"
          style={{
            background: data?.overall === 'operational' ? '#ECFDF5' : data?.overall === 'degraded' ? '#FFFBEB' : '#FEF2F2',
            borderColor: overallMeta.color,
          }}
          data-testid="status-overall-card"
        >
          <p className="text-sm font-medium uppercase tracking-wide" style={{ color: overallMeta.color }}>
            Current status
          </p>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mt-2" style={{ color: '#1A1209' }}>
            {data?.overall_label || 'All systems operational'}
          </h1>
          <p className="text-sm text-gray-600 mt-4 flex items-center gap-2">
            <Clock size={14} />
            <span>Last checked {timeAgo(data?.checked_at)} · auto-refreshes every 30s</span>
          </p>
          {err && (
            <p className="text-xs text-red-600 mt-2" data-testid="status-error">
              {err}
            </p>
          )}
        </div>

        {/* Components */}
        <section className="mb-10" data-testid="status-components-section">
          <h2 className="text-base sm:text-lg font-semibold mb-3" style={{ color: '#1A1209' }}>Components</h2>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
            {(data?.components || []).map((c) => {
              const Icon = COMPONENT_ICON[c.name] || Activity;
              return (
                <div key={c.name} className="flex items-center justify-between px-6 py-4" data-testid={`status-component-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="flex items-center gap-3">
                    <Icon size={18} className="text-gray-500" />
                    <div>
                      <div className="font-medium text-gray-900">{c.name}</div>
                      <div className="text-xs text-gray-500">{c.description}</div>
                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
              );
            })}
          </div>
        </section>

        {/* Stats */}
        <section className="mb-10 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="status-stats">
          <Stat label="Last 24h incidents" value={data?.incidents_24h ?? 0} />
          <Stat label="Last 90d incidents" value={data?.incidents_90d ?? 0} />
          <Stat label="DB ping"
            value={data?.components?.find(c => c.name === 'Database')?.ping_ms != null
              ? `${data.components.find(c => c.name === 'Database').ping_ms} ms`
              : '—'} />
          <Stat label="Build" value={data?.build || 'unknown'} mono />
        </section>

        {/* Trust signals — for investors / board / press (Sprint 203) */}
        {data?.trust && (
          <section className="mb-10" data-testid="status-trust-section">
            <h2 className="text-base sm:text-lg font-semibold mb-3" style={{ color: '#1A1209' }}>Trust snapshot</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="status-stripe-card">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Payments</div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span
                    className="text-lg font-bold"
                    style={{ color: data.trust.stripe_mode === 'live' ? '#059669' : '#B45309' }}
                  >
                    Stripe · {data.trust.stripe_mode === 'live' ? 'LIVE' : 'TEST'}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {data.trust.stripe_live_since
                    ? <>Processing real payments since <strong>{new Date(data.trust.stripe_live_since).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</strong></>
                    : data.trust.stripe_mode === 'live'
                      ? 'Processing real payments.'
                      : 'In test mode.'}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="status-subs-card">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Paying families</div>
                {data.trust.active_subscriptions_approx != null ? (
                  <>
                    <div className="text-lg font-bold text-gray-900 mb-1 tabular-nums">
                      {data.trust.active_subscriptions_approx >= 10 ? `${data.trust.active_subscriptions_approx}+` : data.trust.active_subscriptions_approx}
                    </div>
                    <div className="text-xs text-gray-500">Active subscriptions right now.</div>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-bold text-gray-400 mb-1">—</div>
                    <div className="text-xs text-gray-500">Subscription count unavailable.</div>
                  </>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Robustness — automated 130-route web platform sweep (Sprint 228) */}
        {data?.robustness?.available && (
          <section className="mb-10" data-testid="status-robustness-section">
            <h2 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: '#1A1209' }}>
              <Bot size={20} className="text-emerald-600" />
              Web platform robustness
            </h2>
            <div
              className="bg-white rounded-2xl border p-5 sm:p-6"
              style={{ borderColor: data.robustness.healthy ? '#10B981' : '#F59E0B' }}
              data-testid="status-robustness-card"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span
                      className="text-2xl sm:text-3xl font-bold tabular-nums"
                      style={{ color: data.robustness.healthy ? '#059669' : '#B45309' }}
                      data-testid="status-robustness-label"
                    >
                      {data.robustness.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed max-w-md">
                    Every public + protected route is checked at {data.robustness.viewport} mobile viewport
                    for layout overflow, visual regressions, and 40×40px tap targets.
                    Last sweep: <strong>{timeAgo(data.robustness.generated_at)}</strong>.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Cadence</p>
                  <p className="text-xs text-gray-700 leading-tight">
                    Every PR<br />+ nightly 04:00 UTC
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-5 pt-4 border-t border-gray-100">
                <RobustStat label="Layout overflow" value={data.robustness.fail} ok={data.robustness.fail === 0} tid="status-robust-layout" />
                <RobustStat label="Visual diffs" value={data.robustness.visual_regressions} ok={data.robustness.visual_regressions === 0} tid="status-robust-visual" />
                <RobustStat label="Tap-target fails" value={data.robustness.tap_target_fails} ok={data.robustness.tap_target_fails === 0} tid="status-robust-tap" />
                <RobustStat label="Lighthouse drops" value={data.robustness.lighthouse_fails} ok={data.robustness.lighthouse_fails === 0} tid="status-robust-lh" />
              </div>
            </div>
          </section>
        )}

        {/* CI heartbeat — public-safe view of the internal pytest health.
            Sprint Feb 2026 — "SOC2-lite" trust surface for parents & schools. */}
        {data?.ci_health?.available && (
          <section className="mb-10" data-testid="status-ci-section">
            <h2 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: '#1A1209' }}>
              <FlaskConical size={20} className="text-indigo-600" />
              Test-suite health
            </h2>
            {(() => {
              const stale = data.ci_health.stale && data.ci_health.healthy && (data.ci_health.failed || 0) === 0;
              const borderCol = stale ? '#D97706' : data.ci_health.healthy ? '#10B981' : '#EF4444';
              const labelCol = stale ? '#B45309' : data.ci_health.healthy ? '#059669' : '#DC2626';
              return (
            <div
              className="bg-white rounded-2xl border p-5 sm:p-6"
              style={{ borderColor: borderCol }}
              data-testid="status-ci-card"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span
                      className="text-2xl sm:text-3xl font-bold tabular-nums"
                      style={{ color: labelCol }}
                      data-testid="status-ci-label"
                    >
                      {data.ci_health.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed max-w-md">
                    Every safety guardrail, payment path, auth flow, and Vault operation
                    is exercised by an automated pytest suite. Runs {data.ci_health.cadence}.
                    {data.ci_health.ran_at && (
                      <> Last run <strong>{timeAgo(data.ci_health.ran_at)}</strong>
                        {data.ci_health.duration_s != null && (
                          <> · {Math.round(data.ci_health.duration_s)}s wall time</>
                        )}.</>
                    )}
                    {stale && (
                      <> <span className="text-amber-700 font-semibold">Watchdog: the pipeline hasn&apos;t reported in over {data.ci_health.stale_threshold_hours}h</span> — the numbers below are historical, not live. Investigate the nightly worker before trusting this badge.</>
                    )}
                    {data.ci_health.mirrored_from && (
                      <> <span className="text-indigo-700 font-semibold">Mirrored from preview</span> — production isolates real users, Stripe, and Postmark from the test harness by design.</>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Green streak</p>
                  <p
                    className="text-2xl font-bold tabular-nums"
                    style={{ color: data.ci_health.green_streak > 0 ? '#059669' : '#B45309' }}
                    data-testid="status-ci-green-streak"
                  >
                    {data.ci_health.green_streak}
                    <span className="text-xs font-medium text-gray-500 ml-1">runs</span>
                  </p>
                </div>
              </div>

              {/* Sparkline — last 30 runs */}
              {data.ci_health.history?.length > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">
                      Last {data.ci_health.runs_30d} runs
                    </p>
                    <p className="text-[10px] font-semibold text-gray-500">
                      Pass rate: <span className="tabular-nums text-gray-900">{data.ci_health.pass_rate_30d}%</span>
                    </p>
                  </div>
                  <div className="flex items-end gap-1 h-10" data-testid="status-ci-sparkline">
                    {data.ci_health.history.map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t transition-all hover:opacity-80"
                        style={{
                          height: h.status === 'green' ? '100%' : '40%',
                          backgroundColor: h.status === 'green' ? '#10B981' : '#EF4444',
                          minWidth: '3px',
                        }}
                        title={`${h.ran_at?.slice(0, 10)} — ${h.passed} passed, ${h.failed} failed`}
                        data-testid={`status-ci-spark-${i}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4 pt-4 border-t border-gray-100">
                <CIStat label="Passing" value={data.ci_health.passed ?? '—'} ok tid="status-ci-passed" />
                <CIStat label="Failing" value={data.ci_health.failed ?? '—'} ok={data.ci_health.failed === 0} tid="status-ci-failed" />
                <CIStat label="Skipped" value={data.ci_health.skipped ?? '—'} ok neutral tid="status-ci-skipped" />
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap" data-testid="status-ci-archive-link-row">
                <p className="text-[11px] text-gray-500">
                  Need the full audit trail? Download a SHA-256 signed snapshot from the archive.
                </p>
                <Link
                  to="/status/history"
                  className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                  data-testid="status-ci-archive-link"
                >
                  View 90-day archive →
                </Link>
              </div>

              <BadgeEmbedPanel />
            </div>
              );
            })()}
          </section>
        )}

        {/* Recent incidents */}
        <section data-testid="status-incidents-section">
          <h2 className="text-base sm:text-lg font-semibold mb-3" style={{ color: '#1A1209' }}>Recent incidents (90 days)</h2>
          {incidents.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 px-6 py-10 text-center text-gray-500" data-testid="status-no-incidents">
              <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
              <p>No incidents reported in the last 90 days. ✨</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100" data-testid="status-incidents-list">
              {incidents.map((inc, idx) => {
                const sevColor = inc.level === 'CRITICAL' ? '#EF4444'
                              : inc.level === 'ERROR' ? '#F59E0B'
                              : '#6B7280';
                return (
                  <div key={idx} className="px-6 py-3" data-testid={`status-incident-${idx}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: sevColor }}>
                        {inc.level} · {inc.component}
                      </span>
                      <span className="text-xs text-gray-400">{timeAgo(inc.timestamp)}</span>
                    </div>
                    <p className="text-sm text-gray-700">{inc.message}</p>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="mt-12 text-center text-xs text-gray-500">
          <p>
            Issues to report? Email{' '}
            <a href="mailto:security@hibff.com" className="text-purple-600 hover:underline">
              security@hibff.com
            </a>{' '}
            or read our{' '}
            <a
              href="https://github.com/hibff/hibff/security/policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:underline"
            >
              security policy
            </a>
            .
          </p>
        </footer>
      </main>
    </div>
  );
};

const Stat = ({ label, value, mono }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-2xl font-bold mt-0.5 ${mono ? 'font-mono text-base' : ''}`} style={{ color: '#1A1209' }}>
      {value}
    </p>
  </div>
);

const RobustStat = ({ label, value, ok, tid }) => (
  <div
    className="rounded-lg px-3 py-2 text-center"
    style={{ backgroundColor: ok ? '#ECFDF5' : '#FFFBEB' }}
    data-testid={tid}
  >
    <p className="text-[10px] uppercase tracking-wide text-gray-600 mb-0.5">{label}</p>
    <p className="text-lg font-bold tabular-nums" style={{ color: ok ? '#059669' : '#B45309' }}>{value}</p>
  </div>
);

const CIStat = ({ label, value, ok, neutral, tid }) => (
  <div
    className="rounded-lg px-3 py-2 text-center"
    style={{ backgroundColor: neutral ? '#F3F4F6' : ok ? '#ECFDF5' : '#FEF2F2' }}
    data-testid={tid}
  >
    <p className="text-[10px] uppercase tracking-wide text-gray-600 mb-0.5">{label}</p>
    <p
      className="text-lg font-bold tabular-nums"
      style={{ color: neutral ? '#4B5563' : ok ? '#059669' : '#DC2626' }}
    >
      {value}
    </p>
  </div>
);

// Live shields.io-style embed panel — lets partners, investors, and the
// founder copy-paste the badge into READMEs, App Store listings, decks, etc.
// The badge URL is computed off the current site origin so it works in both
// the preview environment and prod (hibff.com) without config.
const BadgeEmbedPanel = () => {
  const [copied, setCopied] = useState(null); // 'md' | 'html' | 'url' | null
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://hibff.com';
  const badgeUrl = `${origin}/api/status/badge.svg`;
  const statusUrl = `${origin}/status`;
  const markdown = `[![HiBFF Tests](${badgeUrl})](${statusUrl})`;
  const html = `<a href="${statusUrl}"><img src="${badgeUrl}" alt="HiBFF Tests" /></a>`;

  const copy = async (kind, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    } catch (_) { /* clipboard blocked — silent */ }
  };

  return (
    <div className="mt-5 pt-4 border-t border-gray-100" data-testid="status-ci-embed">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Embed this badge</p>
          <p className="text-xs text-gray-600 mt-0.5">Paste into READMEs, App Store listings, partnership PDFs. Live-updates every 60s.</p>
        </div>
        <img
          src={badgeUrl}
          alt="Live HiBFF Tests badge"
          className="h-5 shadow-sm"
          data-testid="status-ci-embed-preview"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <EmbedRow label="Markdown" value={markdown} copied={copied === 'md'} onCopy={() => copy('md', markdown)} tid="embed-markdown" />
        <EmbedRow label="HTML" value={html} copied={copied === 'html'} onCopy={() => copy('html', html)} tid="embed-html" />
      </div>
      <button
        type="button"
        onClick={() => copy('url', badgeUrl)}
        className="mt-2 text-[11px] text-gray-500 hover:text-indigo-700 underline decoration-dotted underline-offset-2"
        data-testid="embed-copy-url"
      >
        {copied === 'url' ? '✓ URL copied' : `Copy raw SVG URL: ${badgeUrl}`}
      </button>
    </div>
  );
};

const EmbedRow = ({ label, value, copied, onCopy, tid }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid={tid}>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-0.5">{label}</p>
      <code className="text-[11px] text-gray-800 font-mono break-all leading-tight block">{value}</code>
    </div>
    <button
      type="button"
      onClick={onCopy}
      className={`text-[11px] font-semibold px-2 py-1 rounded transition ${
        copied ? 'bg-emerald-100 text-emerald-700' : 'bg-white border border-gray-200 text-gray-700 hover:border-indigo-400 hover:text-indigo-700'
      }`}
      data-testid={`${tid}-copy`}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  </div>
);

export default StatusPage;
